import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { customersAPI, invoicesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Loader2, Check, Plus, Trash2, Receipt, Search, User } from 'lucide-react';
import { toast } from 'sonner';

export default function TechNewInvoice() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  // Customer search
  const [customerQuery, setCustomerQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showSearch, setShowSearch] = useState(false);

  // Invoice fields
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([{ name: '', quantity: '1', unit_price: '' }]);

  const searchCustomers = useCallback(async (q) => {
    if (!q || q.length < 2) { setCustomers([]); return; }
    setSearchLoading(true);
    try {
      const res = await customersAPI.list({ search: q, page_size: 10 });
      setCustomers(res.data.customers || []);
    } catch { setCustomers([]); }
    finally { setSearchLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchCustomers(customerQuery), 300);
    return () => clearTimeout(t);
  }, [customerQuery, searchCustomers]);

  const addItem = () => setItems(prev => [...prev, { name: '', quantity: '1', unit_price: '' }]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const updateItem = (idx, field, val) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));

  const validItems = items.filter(i => i.name.trim() && parseFloat(i.unit_price) > 0);
  const canSubmit = validItems.length > 0;

  const subtotal = validItems.reduce((sum, i) => sum + (parseFloat(i.quantity) || 1) * (parseFloat(i.unit_price) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      // Step 1: Create the invoice shell
      const res = await invoicesAPI.create({
        customer_id: selectedCustomer?.id || null,
        description: description.trim() || 'Field Invoice',
        notes: notes.trim() || null,
      });
      const invoiceId = res.data.id;

      // Step 2: Add line items
      for (const [idx, it] of validItems.entries()) {
        await invoicesAPI.addItem(invoiceId, {
          name: it.name.trim(),
          quantity: parseFloat(it.quantity) || 1,
          unit_price: parseFloat(it.unit_price) || 0,
          type: 'service',
          taxable: true,
          sort_order: idx,
        });
      }

      toast.success(`Invoice ${res.data.invoice_number} created`);
      navigate('/tech', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50" data-testid="tech-new-invoice">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200">
        <button onClick={() => navigate(-1)} className="p-1.5 -ml-1 rounded-lg active:bg-slate-100" data-testid="tech-new-invoice-back">
          <ArrowLeft className="w-5 h-5 text-slate-700" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold text-slate-900">New Invoice</h1>
          <p className="text-[11px] text-slate-400">Quick field invoice</p>
        </div>
        <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center">
          <Receipt className="w-4 h-4 text-amber-600" />
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
        <div className="px-4 py-5 space-y-5">
          {/* Customer */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Customer (optional)</p>
            {selectedCustomer ? (
              <div className="flex items-center gap-3 p-2.5 rounded-lg border border-blue-200 bg-blue-50">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-900 truncate">{selectedCustomer.display_name}</p>
                  {selectedCustomer.primary_phone && <p className="text-[11px] text-slate-500">{selectedCustomer.primary_phone}</p>}
                </div>
                <button type="button" onClick={() => { setSelectedCustomer(null); setShowSearch(true); }} className="text-xs text-blue-600 font-bold" data-testid="invoice-change-customer">
                  Change
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setShowSearch(true)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-slate-300 text-sm text-slate-500 active:bg-slate-50"
                  data-testid="invoice-select-customer"
                >
                  <Search className="w-4 h-4" /> Search & link customer
                </button>
                {showSearch && (
                  <div className="space-y-2">
                    <Input
                      autoFocus
                      placeholder="Type name or phone..."
                      value={customerQuery}
                      onChange={(e) => setCustomerQuery(e.target.value)}
                      className="h-9 text-sm bg-slate-50 border-slate-200"
                      data-testid="invoice-customer-search"
                    />
                    {searchLoading && <div className="flex justify-center py-2"><Loader2 className="w-4 h-4 animate-spin text-blue-600" /></div>}
                    {customers.length > 0 && (
                      <div className="max-h-36 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                        {customers.map(c => (
                          <button
                            key={c.id}
                            type="button"
                            className="w-full flex items-center gap-2 px-3 py-2 text-left active:bg-slate-50 text-sm"
                            onClick={() => { setSelectedCustomer(c); setShowSearch(false); setCustomerQuery(''); setCustomers([]); }}
                            data-testid={`invoice-customer-${c.id}`}
                          >
                            <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="font-medium text-slate-800 truncate">{c.display_name}</span>
                            {c.primary_phone && <span className="text-[11px] text-slate-400 ml-auto shrink-0">{c.primary_phone}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                    {customerQuery.length >= 2 && !searchLoading && customers.length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-2">No customers found</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Details */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Details</p>
            <div>
              <Label className="text-xs text-slate-500">Description</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Work performed" rows={2} className="mt-1 text-sm bg-slate-50 border-slate-200 resize-none" data-testid="invoice-description" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes" rows={2} className="mt-1 text-sm bg-slate-50 border-slate-200 resize-none" data-testid="invoice-notes" />
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Line Items</p>
              <button type="button" onClick={addItem} className="flex items-center gap-1 text-xs text-blue-600 font-bold active:text-blue-800" data-testid="invoice-add-item">
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
            <div className="space-y-3">
              {items.map((item, idx) => (
                <div key={idx} className="p-3 rounded-lg border border-slate-200 bg-slate-50 space-y-2" data-testid={`invoice-item-${idx}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <Input
                        placeholder="Service / part name"
                        value={item.name}
                        onChange={(e) => updateItem(idx, 'name', e.target.value)}
                        className="h-9 text-sm bg-white border-slate-200"
                        data-testid={`invoice-item-name-${idx}`}
                      />
                    </div>
                    {items.length > 1 && (
                      <button type="button" onClick={() => removeItem(idx)} className="p-1.5 text-slate-400 active:text-red-500" data-testid={`invoice-item-remove-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] text-slate-400">Qty</Label>
                      <Input type="number" min="1" step="1" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} className="h-9 text-sm bg-white border-slate-200" data-testid={`invoice-item-qty-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-400">Unit Price ($)</Label>
                      <Input type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} placeholder="0.00" className="h-9 text-sm bg-white border-slate-200" data-testid={`invoice-item-price-${idx}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Subtotal */}
            {subtotal > 0 && (
              <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                <span className="text-xs font-medium text-slate-500">Subtotal</span>
                <span className="text-sm font-bold text-slate-900" data-testid="invoice-subtotal">${subtotal.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Sticky submit */}
        <div className="sticky bottom-0 px-4 py-3 bg-white border-t border-slate-200">
          <Button
            type="submit"
            disabled={saving || !canSubmit}
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-sm font-bold rounded-xl"
            data-testid="invoice-submit-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Create Invoice
          </Button>
        </div>
      </form>
    </div>
  );
}
